// src/components/NotaRapidaCard.jsx
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaTrash, FaEllipsisH, FaBold, FaItalic, FaListUl, FaFile,
} from "react-icons/fa";
import { MdPersonRemove } from "react-icons/md";
import { supabase } from "../supabaseClient";
import { podeEnviarNota } from "../utils/solicitacoesEnvio";
import "./NotaRapidaCard.css";

const CORES_CARD = ["#fce4ec", "#e8f5e9", "#e3f2fd", "#fff9e6", "#f3e8fd", "#fdecea", "#e0f7fa", "#eceff1"];
const CORES_TEXTO = ["#1e293b", "#dc2626", "#2563eb", "#16a34a", "#d97706", "#9333ea"];

// ── Altura mínima da nota rápida quando limitada ─────────────────────
const ALTURA_MIN_NOTA = 64;

const TAG_ICON_LINK = {
  "Lista": "list",
  "Atas": "description",
  "Tarefas": "task_alt",
  "Metas": "flag",
};

const notaEdicoesCache = new Map();

function valorComCache(notaId, campo, valorAtual) {
  const cache = notaEdicoesCache.get(notaId);
  if (cache && cache[campo] !== undefined) return cache[campo];
  return valorAtual;
}

function atualizarCache(notaId, patch) {
  const atual = notaEdicoesCache.get(notaId) || {};
  notaEdicoesCache.set(notaId, { ...atual, ...patch });
}

// ── 1. Helpers fora do componente (abaixo de atualizarCache) ──
const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"']+[^\s<>"'.,;:!?)\]}])/gi;

function linkificar(html) {
  if (!html || typeof window === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const raiz = doc.body.firstChild;
  raiz.querySelectorAll("a").forEach((a) => {
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.classList.add("nrc-link");
  });
  const walker = doc.createTreeWalker(raiz, NodeFilter.SHOW_TEXT);
  const nos = [];
  while (walker.nextNode()) {
    if (!walker.currentNode.parentElement.closest("a")) nos.push(walker.currentNode);
  }
  nos.forEach((no) => {
    const texto = no.nodeValue;
    URL_REGEX.lastIndex = 0;
    if (!URL_REGEX.test(texto)) return;
    URL_REGEX.lastIndex = 0;
    const frag = doc.createDocumentFragment();
    let ultimo = 0;
    let m;
    while ((m = URL_REGEX.exec(texto))) {
      frag.appendChild(doc.createTextNode(texto.slice(ultimo, m.index)));
      const a = doc.createElement("a");
      a.href = /^www\./i.test(m[1]) ? `https://${m[1]}` : m[1];
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.className = "nrc-link";
      a.textContent = m[1];
      frag.appendChild(a);
      ultimo = m.index + m[0].length;
    }
    frag.appendChild(doc.createTextNode(texto.slice(ultimo)));
    no.parentNode.replaceChild(frag, no);
  });
  return raiz.innerHTML;
}

const nomeArquivoSeguro = (nome) =>
  nome
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_");

export default function NotaRapidaCard({
  nota,
  onSaveResponsavel,
  onRemoveResponsavel,
  handleDeleteNota,
  menuOpenNota,
  setMenuOpenNota,
  pilhaId,
  dragHandleProps,
  containerId,
  usuarioId,
  entityType,
  entityId,
  flutuante = false,
  onToggleFlutuante,
  onSalvarPosicao,
  onRedimensionar,
  onOpenTask,
  notasDisponiveis = [],
  onSavePrioridade,
  onEnviarNota,
}) {
  const [titulo, setTitulo] = useState(() => valorComCache(nota.id, "nome", nota.nome || ""));
  const [isEditingTitulo, setIsEditingTitulo] = useState(false);

  const [descricaoHtml, setDescricaoHtml] = useState(() => valorComCache(nota.id, "descricao", nota.descricao || ""));
  const [isEditingDescricao, setIsEditingDescricao] = useState(false);
  const descRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [imagemUrl, setImagemUrl] = useState(() => valorComCache(nota.id, "imagem_url", nota.imagem_url || ""));

  // ── Modal de imagem + comentários ──
  const [showImagemModal, setShowImagemModal] = useState(false);
  const [comentarios, setComentarios] = useState([]);
  const [novoComentario, setNovoComentario] = useState("");
  const [enviandoComentario, setEnviandoComentario] = useState(false);

  const [corCard, setCorCard] = useState(() => valorComCache(nota.id, "cor", nota.cor || CORES_CARD[0]));
  const [showCorPicker, setShowCorPicker] = useState(false);

  const PRIORIDADE_CONFIG = {
    alta:  { label: "Alta",  color: "#dc2626", bg: "#fef2f2" },
    media: { label: "Média", color: "#d97706", bg: "#fffbeb" },
    baixa: { label: "Baixa", color: "#16a34a", bg: "#f0fdf4" },
  };

  const [dragOverPrioridade, setDragOverPrioridade] = useState(false);

  const handleDragOverPrioridade = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOverPrioridade(true);
  };

  const handleDragLeavePrioridade = () => setDragOverPrioridade(false);

  const handleDropPrioridade = async (e) => {
    e.preventDefault();
    setDragOverPrioridade(false);
    const prioridade = e.dataTransfer.getData("prioridade");
    if (!prioridade) return;
    await onSavePrioridade?.(nota.id, prioridade);
  };

  const prioInfo = nota.prioridade ? PRIORIDADE_CONFIG[nota.prioridade] : null;

  const [chamaAtencao, setChamaAtencao] = useState(() => !!valorComCache(nota.id, "chama_atencao", nota.chama_atencao));
  const [piscando, setPiscando] = useState(!!nota.chama_atencao);

  const [anexos, setAnexos] = useState([]);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);

  const [showResponsavelInput, setShowResponsavelInput] = useState(false);
  const [inputResponsavel, setInputResponsavel] = useState("");
  const [sugestoesResponsavel, setSugestoesResponsavel] = useState([]);
  const inputRef = useRef(null);
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });

  const [avataresResponsaveis, setAvataresResponsaveis] = useState([]);

  const [dataEntrega, setDataEntrega] = useState(() => valorComCache(nota.id, "data_entrega", nota.data_entrega || ""));
  const [isEditingDataEntrega, setIsEditingDataEntrega] = useState(false);

  const [linksVinculados, setLinksVinculados] = useState([]);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [buscaLink, setBuscaLink] = useState("");
  const linkPopoverRef = useRef(null);

  const cardRef = useRef(null);
  const [posLocal, setPosLocal] = useState({ x: nota.pos_x ?? 80, y: nota.pos_y ?? 80 });

  const [alturaCustom, setAlturaCustom] = useState(() => valorComCache(nota.id, "altura_pilha", nota.altura_pilha ?? null));
  const [redimensionando, setRedimensionando] = useState(false);
  const [temOverflow, setTemOverflow] = useState(false);

  useEffect(() => {
    setPosLocal({ x: nota.pos_x ?? 80, y: nota.pos_y ?? 80 });
  }, [nota.pos_x, nota.pos_y]);

  useEffect(() => {
    setPiscando(!!nota.chama_atencao);
  }, [nota.id]);

  useEffect(() => {
    if (!piscando) return;
    const t = setTimeout(() => setPiscando(false), 8000);
    return () => clearTimeout(t);
  }, [piscando]);

  useEffect(() => {
    const fetchAvatares = async () => {
      const ids = nota.responsaveis_ids || [];
      if (ids.length === 0) { setAvataresResponsaveis([]); return; }

      const [{ data: profiles }, { data: convitesAtivos }] = await Promise.all([
        supabase.from("profiles").select("id, avatar_url, nome").in("id", ids),
        containerId
          ? supabase.from("convites").select("user_id").eq("container_id", containerId).eq("status", "aceito")
          : Promise.resolve({ data: [] }),
      ]);

      const ativosSet = new Set([containerId, ...(convitesAtivos || []).map(c => c.user_id).filter(Boolean)]);
      setAvataresResponsaveis(
        (profiles || []).map(p => ({ ...p, removido: !ativosSet.has(p.id) }))
      );
    };
    fetchAvatares();
  }, [nota.responsaveis_ids, containerId]);

  useEffect(() => {
    setTitulo(valorComCache(nota.id, "nome", nota.nome || ""));
    setDescricaoHtml(valorComCache(nota.id, "descricao", nota.descricao || ""));
    setImagemUrl(valorComCache(nota.id, "imagem_url", nota.imagem_url || ""));
    setDataEntrega(valorComCache(nota.id, "data_entrega", nota.data_entrega || ""));
    setCorCard(valorComCache(nota.id, "cor", nota.cor || CORES_CARD[0]));
    setAlturaCustom(valorComCache(nota.id, "altura_pilha", nota.altura_pilha ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nota.id]);

  const buscarAnexos = async () => {
    const { data } = await supabase
      .from("anexos")
      .select("id, nota_id, file_url, file_name, mime_type")
      .eq("nota_id", nota.id);
    setAnexos(data || []);
  };

  useEffect(() => { buscarAnexos(); }, [nota.id]);

  const buscarLinks = async () => {
    const { data } = await supabase
      .from("nota_links")
      .select("id, linked_nota_id, notas:linked_nota_id (id, nome, tipo)")
      .eq("nota_id", nota.id);
    setLinksVinculados(data || []);
  };

  useEffect(() => { buscarLinks(); }, [nota.id]);

  const alternarLink = async (notaAlvo) => {
    const existente = linksVinculados.find(l => l.linked_nota_id === notaAlvo.id);
    if (existente) {
      await supabase.from("nota_links").delete().eq("id", existente.id);
    } else {
      await supabase.from("nota_links").insert([{ nota_id: nota.id, linked_nota_id: notaAlvo.id }]);
    }
    buscarLinks();
  };

  // ── Comentários da imagem ──
  const buscarComentarios = async () => {
    const { data } = await supabase
      .from("nota_comentarios")
      .select("id, nota_id, user_id, texto, created_at, profiles:user_id (nome, avatar_url)")
      .eq("nota_id", nota.id)
      .order("created_at", { ascending: true });
    setComentarios(data || []);
  };

  useEffect(() => { buscarComentarios(); }, [nota.id]);

  const enviarComentario = async () => {
    const texto = novoComentario.trim();
    if (!texto || !usuarioId || enviandoComentario) return;
    setEnviandoComentario(true);
    const { error } = await supabase
      .from("nota_comentarios")
      .insert([{ nota_id: nota.id, user_id: usuarioId, texto }]);
    if (!error) {
      setNovoComentario("");
      await buscarComentarios();
    }
    setEnviandoComentario(false);
  };

  const excluirComentario = async (id) => {
    if (!window.confirm("Excluir este comentário?")) return;
    await supabase.from("nota_comentarios").delete().eq("id", id);
    buscarComentarios();
  };

  useEffect(() => {
    if (!showImagemModal) return;
    const onKey = (e) => { if (e.key === "Escape") setShowImagemModal(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showImagemModal]);

  useEffect(() => {
    if (!showLinkPicker) return;
    const handleClick = (e) => {
      if (linkPopoverRef.current && !linkPopoverRef.current.contains(e.target)) setShowLinkPicker(false);
    };
    const t = setTimeout(() => document.addEventListener("mousedown", handleClick), 100);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handleClick); };
  }, [showLinkPicker]);

  useEffect(() => {
    if (!inputResponsavel.startsWith("@") || inputResponsavel.length <= 1 || !containerId) {
      setSugestoesResponsavel([]);
      return;
    }
    const termo = inputResponsavel.slice(1).trim().toLowerCase();
    if (!termo) { setSugestoesResponsavel([]); return; }
    const fetchSugestoes = async () => {
      const { data: convites } = await supabase
        .from("convites")
        .select("user_id")
        .eq("container_id", containerId)
        .eq("status", "aceito")
        .not("user_id", "is", null);
      if (!convites?.length) { setSugestoesResponsavel([]); return; }
      const userIds = [...new Set(convites.map((c) => c.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, nickname, nome")
        .in("id", userIds);
      const sugestoes = (profiles || [])
        .filter((p) =>
          (p.nickname && p.nickname.toLowerCase().includes(termo)) ||
          (p.nome && p.nome.toLowerCase().includes(termo))
        )
        .slice(0, 10);
      setSugestoesResponsavel(sugestoes);
    };
    const debounce = setTimeout(fetchSugestoes, 150);
    return () => clearTimeout(debounce);
  }, [inputResponsavel, containerId]);

  useEffect(() => {
    if (!showResponsavelInput) return;
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowResponsavelInput(false);
        setInputResponsavel("");
        setSugestoesResponsavel([]);
      }
    };
    const timer = setTimeout(() => document.addEventListener("mousedown", handleClickOutside), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showResponsavelInput]);

  const pararPiscar = () => { if (piscando) setPiscando(false); };

  const salvarTitulo = async () => {
    setIsEditingTitulo(false);
    const novo = titulo.trim();
    if (novo === nota.nome) return;
    const nomeFinal = novo || "Sem título";
    atualizarCache(nota.id, { nome: nomeFinal });
    await supabase.from("notas").update({ nome: nomeFinal }).eq("id", nota.id);
  };

  const salvarDescricao = async () => {
    const html = descRef.current?.innerHTML || "";
    setDescricaoHtml(html);
    setIsEditingDescricao(false);
    atualizarCache(nota.id, { descricao: html });
    if (html === nota.descricao) return;
    await supabase.from("notas").update({ descricao: html }).eq("id", nota.id);
  };

  const abrirEdicaoDescricao = () => {
    setIsEditingDescricao(true);
    setTimeout(() => {
      if (descRef.current) {
        descRef.current.innerHTML = descricaoHtml;
        descRef.current.focus();
      }
    }, 0);
  };

  const aplicarFormatacao = (comando, valor = null) => {
    document.execCommand(comando, false, valor);
    descRef.current?.focus();
  };

  const aplicarAlinhamento = (alinhamento) => {
    document.execCommand("justify" + alinhamento, false, null);
    descRef.current?.focus();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `${nota.id}_${Date.now()}.${fileExt}`;
    const filePath = `quick-notes/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from("notas-imagens")
      .upload(filePath, file, { upsert: true });
    if (uploadError) { setUploading(false); return; }
    const { data } = await supabase.storage.from("notas-imagens").getPublicUrl(filePath);
    if (data?.publicUrl) {
      const { error: updateError } = await supabase
        .from("notas")
        .update({ imagem_url: data.publicUrl })
        .eq("id", nota.id);
      if (!updateError) {
        setImagemUrl(data.publicUrl);
        atualizarCache(nota.id, { imagem_url: data.publicUrl });
      }
    }
    setUploading(false);
  };

  const handleRemoveImage = async () => {
    if (!window.confirm("Remover esta imagem?")) return;
    const { error } = await supabase.from("notas").update({ imagem_url: null }).eq("id", nota.id);
    if (!error) {
      setImagemUrl("");
      atualizarCache(nota.id, { imagem_url: "" });
    }
  };

  const handleAnexoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAnexo(true);
    // ── 3. Nome de arquivo seguro (remove acentos/Ç) ──
    const fileName = `${nota.id}_${Date.now()}_${nomeArquivoSeguro(file.name)}`;
    const filePath = `notas-anexos/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from("notas-anexos")
      .upload(filePath, file, { upsert: true });
    if (uploadError) {
      console.error("Erro upload anexo:", uploadError);
      alert("Erro ao enviar anexo: " + uploadError.message);
      setUploadingAnexo(false);
      return;
    }
    const { data } = await supabase.storage.from("notas-anexos").getPublicUrl(filePath);
    if (data?.publicUrl) {
      const { error: insertError } = await supabase.from("anexos").insert([
        { nota_id: nota.id, user_id: usuarioId, file_url: data.publicUrl, file_name: file.name, mime_type: file.type },
      ]);
      if (insertError) {
        console.error("Erro ao salvar anexo:", insertError);
        alert("Erro ao salvar anexo: " + insertError.message);
      } else {
        buscarAnexos();
      }
    }
    setUploadingAnexo(false);
  };

  const handleRemoveAnexo = async (anexoId) => {
    if (!window.confirm("Remover este anexo?")) return;
    await supabase.from("anexos").delete().eq("id", anexoId);
    buscarAnexos();
  };

  const salvarCor = async (cor) => {
    setCorCard(cor);
    setShowCorPicker(false);
    setMenuOpenNota(null);
    atualizarCache(nota.id, { cor });
    await supabase.from("notas").update({ cor }).eq("id", nota.id);
  };

  const toggleAtencao = async () => {
    const novo = !chamaAtencao;
    setChamaAtencao(novo);
    setPiscando(novo);
    atualizarCache(nota.id, { chama_atencao: novo });
    await supabase.from("notas").update({ chama_atencao: novo }).eq("id", nota.id);
  };

  const salvarDataEntrega = async () => {
    setIsEditingDataEntrega(false);
    if (dataEntrega === nota.data_entrega) return;
    atualizarCache(nota.id, { data_entrega: dataEntrega || null });
    await supabase.from("notas").update({ data_entrega: dataEntrega || null }).eq("id", nota.id);
  };

  const formatarDataLocal = (d) => {
    if (!d) return null;
    const [a, m, dia] = d.split('-');
    return new Date(a, m - 1, dia).toLocaleDateString("pt-BR");
  };

  const criarNotificacaoResponsavel = async (destinatarioId, remetenteId, nomeRemetente) => {
    if (destinatarioId === remetenteId) return;
    const nomeNota = (nota.nome || "sem título").substring(0, 50);
    const mensagem = `${nomeRemetente} te marcou como responsável na nota rápida "${nomeNota}${(nota.nome || "").length > 50 ? "..." : ""}".`;
    const camposEntidade = {};
    if (entityType === "project") camposEntidade.projeto_id = entityId;
    else if (entityType === "setor") camposEntidade.setor_id = entityId;
    await supabase.from("notificacoes").insert([{
      user_id: destinatarioId, remetente_id: remetenteId, mensagem, container_id: containerId,
      pilha_id: pilhaId, nota_id: nota.id, lido: false, tipo: "mencao_responsavel_nota_rapida",
      created_at: new Date().toISOString(), ...camposEntidade,
    }]);
  };

  const handleAddResponsavelClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setInputPosition({ x: rect.left + window.scrollX, y: rect.bottom + window.scrollY + 4 });
    setShowResponsavelInput(true);
    setInputResponsavel("@");
  };

  const handleSelectResponsavel = async (user) => {
    const idsExistentes = nota.responsaveis_ids || [];
    const jaTem = idsExistentes.includes(user.id);
    onSaveResponsavel(nota.id, user.id, user.nickname || user.nome);
    if (!jaTem && usuarioId && entityId && entityType) {
      let nomeRemetente = "Alguém";
      const { data: perfilRemetente } = await supabase
        .from("profiles").select("nickname, nome").eq("id", usuarioId).single();
      if (perfilRemetente) nomeRemetente = perfilRemetente.nickname || perfilRemetente.nome || "Alguém";
      await criarNotificacaoResponsavel(user.id, usuarioId, nomeRemetente);
    }
    setInputResponsavel("@");
    setSugestoesResponsavel([]);
  };

  const handleKeyDownResponsavel = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (sugestoesResponsavel.length === 1) handleSelectResponsavel(sugestoesResponsavel[0]);
    }
  };

  const handleExcluirNota = () => {
    if (window.confirm("Excluir esta nota?")) {
      setMenuOpenNota(null);
      notaEdicoesCache.delete(nota.id);
      handleDeleteNota(nota.id, pilhaId);
    }
  };

  const handleRemoverResponsavel = async (e, userId) => {
    e.stopPropagation();
    if (window.confirm("Remover este responsável?")) {
      onRemoveResponsavel(nota.id, userId);
      setMenuOpenNota(null);
    }
  };

  const handleHeaderMouseDown = (e) => {
    if (!flutuante) return;
    if (e.target.closest("button")) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = posLocal.x;
    const origY = posLocal.y;

    const onMove = (ev) => {
      setPosLocal({ x: origX + (ev.clientX - startX), y: origY + (ev.clientY - startY) });
    };
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const novoX = origX + (ev.clientX - startX);
      const novoY = origY + (ev.clientY - startY);
      setPosLocal({ x: novoX, y: novoY });
      onSalvarPosicao?.(novoX, novoY);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleResizeMouseUp = () => {
    if (!flutuante || !cardRef.current) return;
    const { width, height } = cardRef.current.getBoundingClientRect();
    onRedimensionar?.(Math.round(width), Math.round(height));
  };

  const handleToggleFlutuanteClick = (e) => {
    e.stopPropagation();
    if (isEditingDescricao) salvarDescricao();
    if (isEditingTitulo) salvarTitulo();
    onToggleFlutuante?.();
  };

  const limitada = !flutuante && alturaCustom != null;
  const limitadoAtivo = limitada && !isEditingTitulo && !isEditingDescricao && !showLinkPicker && !showCorPicker && menuOpenNota !== nota.id;

  const verificarOverflow = () => {
    const el = cardRef.current;
    if (!el || !limitadoAtivo) { setTemOverflow(false); return; }
    setTemOverflow(el.scrollHeight > el.clientHeight + 1);
  };

  useLayoutEffect(() => { verificarOverflow(); });

  const salvarAlturaPilha = async (valor) => {
    atualizarCache(nota.id, { altura_pilha: valor });
    await supabase.from("notas").update({ altura_pilha: valor }).eq("id", nota.id);
  };

  const iniciarResizeAltura = (e, borda) => {
    e.preventDefault();
    e.stopPropagation();
    const el = cardRef.current;
    if (!el) return;
    const startY = e.clientY;
    const startH = el.offsetHeight;
    const natural = Math.max(el.scrollHeight, el.offsetHeight);
    let atual = startH;
    setRedimensionando(true);

    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      atual = Math.round(startH + (borda === "bottom" ? dy : -dy));
      atual = Math.max(ALTURA_MIN_NOTA, Math.min(atual, natural));
      setAlturaCustom(atual);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setRedimensionando(false);
      if (atual >= natural - 2) {
        setAlturaCustom(null);
        salvarAlturaPilha(null);
      } else {
        salvarAlturaPilha(atual);
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const expandirNota = (e) => {
    e.stopPropagation();
    setAlturaCustom(null);
    salvarAlturaPilha(null);
  };

  // ── Baixar imagem ──
  const baixarImagem = async (e) => {
    e?.stopPropagation();
    if (!imagemUrl) return;
    try {
      const resp = await fetch(imagemUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = decodeURIComponent(imagemUrl.split("/").pop().split("?")[0]) || "imagem";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(imagemUrl, "_blank");
    }
  };

  const cardConteudo = (
    <div
      ref={cardRef}
      className={`nrc-card ${piscando ? "nrc-card--piscando" : ""} ${flutuante ? "nrc-card--flutuante" : ""} ${dragOverPrioridade ? "nrc-card--drop-over" : ""} ${limitada ? "nrc-card--limitada" : ""}`}
      style={{
        "--nrc-cor": corCard,
        background: corCard,
        ...(flutuante ? { width: "100%", height: "100%" } : {}),
        ...(limitadoAtivo ? { height: alturaCustom, overflow: "hidden" } : {}),
      }}
      {...(!flutuante ? dragHandleProps : {})}
      onClick={pararPiscar}
      onMouseUp={flutuante ? handleResizeMouseUp : undefined}
      onDragOver={handleDragOverPrioridade}
      onDragLeave={handleDragLeavePrioridade}
      onDrop={handleDropPrioridade}
    >
      {!flutuante && (
        <>
          <button
            type="button"
            className="nrc-resize-handle nrc-resize-handle--top"
            title="Arraste para ajustar a altura"
            onMouseDown={(e) => iniciarResizeAltura(e, "top")}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="nrc-resize-handle nrc-resize-handle--bottom"
            title="Arraste para ajustar a altura"
            onMouseDown={(e) => iniciarResizeAltura(e, "bottom")}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      )}
      {limitadoAtivo && temOverflow && !redimensionando && (
        <button type="button" className="nrc-expandir-btn" onClick={expandirNota} title="Expandir nota">
          <span className="material-symbols-outlined">expand_more</span>
          Expandir
        </button>
      )}

      {!flutuante && avataresResponsaveis.length > 0 && (
        <div className="nrc-responsaveis-corner">
          {avataresResponsaveis.map((r, i) => (
            <div
              key={r.id}
              className={`nrc-responsavel-avatar ${r.removido ? "nrc-responsavel-avatar--removido" : ""}`}
              style={{ zIndex: avataresResponsaveis.length - i, right: i * 14 }}
              title={r.removido ? `Usuário removido do container — ${r.nome}` : r.nome}
            >
              {r.removido ? (
                <span className="nrc-responsavel-sombra">?</span>
              ) : r.avatar_url ? (
                <img src={r.avatar_url} alt={r.nome} />
              ) : (
                <span>{(r.nome || "?").charAt(0).toUpperCase()}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        className="nrc-header"
        onMouseDown={flutuante ? handleHeaderMouseDown : undefined}
        style={flutuante ? { cursor: "grab" } : undefined}
      >
        <span className="nrc-tag">
          <span className="material-symbols-outlined">sticky_note_2</span>
          Nota Rápida
        </span>

        {prioInfo && (
          <span
            className="nrc-priority-badge"
            style={{ "--pri-color": prioInfo.color, "--pri-bg": prioInfo.bg }}
            title={`${prioInfo.label} Prioridade — clique para remover`}
            onClick={async (e) => { e.stopPropagation(); await onSavePrioridade?.(nota.id, null); }}
          >
            <span className="material-symbols-outlined">flag</span>
            {prioInfo.label}
          </span>
        )}

        <button
          className="nrc-menu-btn"
          onClick={(e) => { e.stopPropagation(); setMenuOpenNota(menuOpenNota === nota.id ? null : nota.id); }}
          title="Mais ações"
        >
          <FaEllipsisH size={13} />
        </button>

        {menuOpenNota === nota.id && (
          <div className="nrc-menu-dropdown">
            <button onClick={(e) => { e.stopPropagation(); setShowCorPicker((v) => !v); }}>
              <span className="material-symbols-outlined">palette</span>
              Mudar cor
            </button>

            {showCorPicker && (
              <div className="nrc-cor-picker-inline">
                {CORES_CARD.map((c) => (
                  <button
                    key={c}
                    className={`nrc-cor-swatch ${corCard === c ? "nrc-cor-swatch--ativa" : ""}`}
                    style={{ background: c }}
                    onClick={() => salvarCor(c)}
                    title={`Cor ${CORES_CARD.indexOf(c) + 1}`}
                  />
                ))}
              </div>
            )}

            {avataresResponsaveis.length > 0 && (
              <div className="nrc-menu-responsaveis-lista">
                {avataresResponsaveis.map((r) => (
                  <button key={r.id} onClick={(e) => handleRemoverResponsavel(e, r.id)}>
                    <MdPersonRemove size={14} /> {r.nome}
                  </button>
                ))}
              </div>
            )}
            <button onClick={handleExcluirNota}>
              <FaTrash size={12} /> Excluir
            </button>
          </div>
        )}
      </div>

      {isEditingTitulo ? (
        <input
          className="nrc-titulo-input"
          value={titulo}
          autoFocus
          onChange={(e) => setTitulo(e.target.value)}
          onBlur={salvarTitulo}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <h4 className="nrc-titulo" onClick={(e) => { e.stopPropagation(); setIsEditingTitulo(true); }}>
          {titulo || "Sem título"}
        </h4>
      )}

      {/* ── Imagem com botões de comentário e download ── */}
      {imagemUrl && (
        <div className="nrc-image-wrapper">
          <img
            src={imagemUrl}
            alt=""
            className="nrc-image"
            onLoad={verificarOverflow}
            onClick={(e) => { e.stopPropagation(); setShowImagemModal(true); }}
          />
          <div className="nrc-img-actions">
            <button type="button" className="nrc-img-comment-btn" title="Comentários" onClick={(e) => { e.stopPropagation(); setShowImagemModal(true); }}>
              <span className="material-symbols-outlined">chat_bubble</span>
              {comentarios.length > 0 && <span>{comentarios.length}</span>}
            </button>
            <button type="button" className="nrc-img-comment-btn" title="Baixar imagem" onClick={baixarImagem}>
              <span className="material-symbols-outlined">download</span>
            </button>
          </div>
        </div>
      )}

      {isEditingDescricao ? (
        <div className="nrc-desc-edit-wrapper" onClick={(e) => e.stopPropagation()}>
          <div ref={descRef} className="nrc-desc-editable" contentEditable suppressContentEditableWarning onBlur={salvarDescricao} />
          <div className="nrc-toolbar">
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFormatacao("bold")} title="Negrito"><FaBold size={12} /></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFormatacao("italic")} title="Itálico"><FaItalic size={12} /></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFormatacao("insertUnorderedList")} title="Lista"><FaListUl size={12} /></button>
            <span className="nrc-toolbar-divider" />
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarAlinhamento("Left")} title="Alinhar à esquerda"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>format_align_left</span></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarAlinhamento("Center")} title="Centralizar"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>format_align_center</span></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarAlinhamento("Right")} title="Alinhar à direita"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>format_align_right</span></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarAlinhamento("Justify")} title="Justificado"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>format_align_justify</span></button>
            <span className="nrc-toolbar-divider" />
            {CORES_TEXTO.map((c) => (
              <button key={c} type="button" className="nrc-toolbar-cor" style={{ background: c }} onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFormatacao("foreColor", c)} />
            ))}
          </div>
        </div>
      ) : (
        // ── 2. Descrição renderizada com links clicáveis ──
        <div
          className="nrc-desc-rendered"
          onClick={(e) => {
            e.stopPropagation();
            if (e.target.closest("a")) return;
            abrirEdicaoDescricao();
          }}
          dangerouslySetInnerHTML={{ __html: linkificar(descricaoHtml) || "<span class='nrc-desc-placeholder'>Clique para adicionar descrição...</span>" }}
        />
      )}

      {anexos.length > 0 && (
        <div className="nrc-anexos-lista" onClick={(e) => e.stopPropagation()}>
          {anexos.map((a) => (
            <div key={a.id} className="nrc-anexo-item">
              <FaFile size={12} />
              <a href={a.file_url} target="_blank" rel="noopener noreferrer">{a.file_name}</a>
              <button onClick={() => handleRemoveAnexo(a.id)}><FaTrash size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {linksVinculados.length > 0 && (
        <div className="nrc-links-lista" onClick={(e) => e.stopPropagation()}>
          {linksVinculados.map((l) => (
            <button key={l.id} className="nrc-link-chip" onClick={() => onOpenTask?.(l.linked_nota_id)}>
              <span className="material-symbols-outlined">{TAG_ICON_LINK[l.notas?.tipo] || "link"}</span>
              {l.notas?.nome || "Nota"}
            </button>
          ))}
        </div>
      )}

      <div className="class">

      <div className="nrc-data-row" onClick={(e) => e.stopPropagation()}>
        {isEditingDataEntrega ? (
          <div className="nrc-date-input-row">
            <span className="material-symbols-outlined" style={{ fontSize: 13, color: "#9ca3af" }}>calendar_today</span>
            <input type="date" value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} autoFocus />
            <button style={{ color: "#22c55e" }} onClick={() => salvarDataEntrega()}>✓</button>
            <button style={{ color: "#ef4444" }} onClick={() => { setDataEntrega(nota.data_entrega || ""); setIsEditingDataEntrega(false); }}>✖</button>
          </div>
        ) : (
          <div className="kcard__badge" onClick={() => setIsEditingDataEntrega(true)}>
            <span className="material-symbols-outlined">calendar_today</span>
            {dataEntrega ? formatarDataLocal(dataEntrega) : "Entrega"}
          </div>
        )}
      </div>

      <div className="nrc-footer" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
        <label className="nrc-icon-btn" title="Adicionar imagem">
          {uploading ? <span className="nrc-loading-dots">...</span> : <span className="material-symbols-outlined">image</span>}
          <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} />
        </label>

        {imagemUrl && (
          <button className="nrc-icon-btn nrc-icon-btn--danger" onClick={handleRemoveImage} title="Remover imagem">
            <span className="material-symbols-outlined">delete</span>
          </button>
        )}

        <label className="nrc-icon-btn" title="Anexar arquivo">
          {uploadingAnexo ? <span className="nrc-loading-dots">...</span> : <span className="material-symbols-outlined">attach_file</span>}
          <input type="file" onChange={handleAnexoUpload} style={{ display: "none" }} />
        </label>

        <button className="nrc-icon-btn" onClick={handleAddResponsavelClick} title="Adicionar responsável">
          <span className="material-symbols-outlined">person_add</span>
        </button>

        <button className="nrc-icon-btn" onClick={(e) => { e.stopPropagation(); setShowLinkPicker((v) => !v); }} title="Vincular nota">
          <span className="material-symbols-outlined">link</span>
        </button>

        {onEnviarNota && podeEnviarNota(nota) && (
          <button className="nrc-icon-btn" onClick={(e) => { e.stopPropagation(); onEnviarNota(nota); }} title="Enviar nota">
            <span className="material-symbols-outlined">ios_share</span>
          </button>
        )}

        <button
          className={`nrc-icon-btn ${chamaAtencao ? "nrc-icon-btn--active" : ""}`}
          onClick={toggleAtencao}
          title={chamaAtencao ? "Chamar atenção: ativado" : "Chamar atenção: desativado"}
        >
          <span className="material-symbols-outlined">notifications</span>
        </button>

        <button
          className="nrc-icon-btn"
          onClick={handleToggleFlutuanteClick}
          title={flutuante ? "Fixar na pilha" : "Soltar da pilha"}
        >
          {flutuante ? (
            <span className="material-symbols-outlined">push_pin</span>
          ) : (
            <span className="nrc-emoji-icon">🎈</span>
          )}
        </button>

        {showLinkPicker && (
          <div className="nrc-link-popover" ref={linkPopoverRef}>
            <input
              type="text"
              placeholder="Buscar tarefa, lista, meta ou ata..."
              value={buscaLink}
              onChange={(e) => setBuscaLink(e.target.value)}
              autoFocus
            />
            <div className="nrc-link-popover-lista">
              {notasDisponiveis
                .filter((n) => n.nome?.toLowerCase().includes(buscaLink.toLowerCase()))
                .slice(0, 30)
                .map((n) => {
                  const vinculada = linksVinculados.some((l) => l.linked_nota_id === n.id);
                  return (
                    <div key={n.id} className={`nrc-link-popover-item ${vinculada ? "vinculada" : ""}`} onClick={() => alternarLink(n)}>
                      <span className="material-symbols-outlined">{TAG_ICON_LINK[n.tipo] || "description"}</span>
                      <span>{n.nome}</span>
                      {vinculada && <span className="material-symbols-outlined nrc-link-check">check</span>}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );

  return (
    <>
      {flutuante ? (
        <div
          style={{
            position: "absolute",
            left: posLocal.x,
            top: posLocal.y,
            width: nota.largura || 260,
            height: nota.altura || 220,
            zIndex: 50,
          }}
        >
          {cardConteudo}
          {avataresResponsaveis.length > 0 && (
            <div className="nrc-responsaveis-corner">
              {avataresResponsaveis.map((r, i) => (
                <div
                  key={r.id}
                  className={`nrc-responsavel-avatar ${r.removido ? "nrc-responsavel-avatar--removido" : ""}`}
                  style={{ zIndex: avataresResponsaveis.length - i, right: i * 14 }}
                  title={r.removido ? `Usuário removido do container — ${r.nome}` : r.nome}
                >
                  {r.removido ? (
                    <span className="nrc-responsavel-sombra">?</span>
                  ) : r.avatar_url ? (
                    <img src={r.avatar_url} alt={r.nome} />
                  ) : (
                    <span>{(r.nome || "?").charAt(0).toUpperCase()}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        cardConteudo
      )}

      {showResponsavelInput && createPortal(
        <div
          ref={inputRef}
          style={{ position: "fixed", left: `${inputPosition.x}px`, top: `${inputPosition.y}px`, width: "220px", zIndex: 10000, background: "white", borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", padding: "4px", fontSize: "0.9em" }}
        >
          <input
            type="text"
            value={inputResponsavel}
            onChange={(e) => setInputResponsavel(e.target.value)}
            onKeyDown={handleKeyDownResponsavel}
            placeholder="Ex: @joao"
            autoFocus
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
          />
          {sugestoesResponsavel.length > 0 && (
            <div style={{ marginTop: "4px", maxHeight: "140px", overflowY: "auto", border: "1px solid #eee", borderRadius: "4px", background: "white" }}>
              {sugestoesResponsavel.map((user) => (
                <div
                  key={user.id}
                  onClick={() => handleSelectResponsavel(user)}
                  style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f9ff")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                >
                  @{user.nickname || user.nome}
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* ── Modal de imagem + comentários ── */}
      {showImagemModal && imagemUrl && createPortal(
        <div
          className="nrc-img-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowImagemModal(false); }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="nrc-img-modal">
            <button type="button" className="nrc-img-modal-close" onClick={() => setShowImagemModal(false)} title="Fechar">
              <span className="material-symbols-outlined">close</span>
            </button>

            <div className="nrc-img-modal-imagem">
              <img src={imagemUrl} alt="" className="nrc-img-modal-bg" aria-hidden="true" />
              <img src={imagemUrl} alt="" className="nrc-img-modal-img" />
              <button type="button" className="nrc-img-modal-download" title="Baixar imagem" onClick={baixarImagem}>
                <span className="material-symbols-outlined">download</span>
              </button>
            </div>

            <div className="nrc-img-modal-comentarios">
              <div className="nrc-img-modal-titulo">
                <span className="material-symbols-outlined">chat_bubble</span>
                Comentários ({comentarios.length})
              </div>

              <div className="nrc-img-modal-lista">
                {comentarios.length === 0 && (
                  <div className="nrc-img-modal-vazio">Nenhum comentário ainda.</div>
                )}
                {comentarios.map((c) => (
                  <div key={c.id} className="nrc-comentario">
                    <div className="nrc-comentario-avatar">
                      {c.profiles?.avatar_url ? (
                        <img src={c.profiles.avatar_url} alt="" />
                      ) : (
                        <span>{(c.profiles?.nome || "?").charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="nrc-comentario-corpo">
                      <div className="nrc-comentario-meta">
                        <strong>{c.profiles?.nome || "Usuário"}</strong>
                        <small>{new Date(c.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</small>
                        {c.user_id === usuarioId && (
                          <button type="button" onClick={() => excluirComentario(c.id)} title="Excluir">
                            <FaTrash size={10} />
                          </button>
                        )}
                      </div>
                      <div className="nrc-comentario-texto">{c.texto}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="nrc-img-modal-input">
                <textarea
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarComentario(); }
                  }}
                  placeholder="Escreva um comentário..."
                  rows={2}
                />
                <button type="button" onClick={enviarComentario} disabled={!novoComentario.trim() || enviandoComentario}>
                  <span className="material-symbols-outlined">send</span>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}